// ─────────────────────────────────────────────────────────────
//  dialog-action.bridge.ts
//  Concrete implementation of DialogActionBridge.
//  Responsibilities:
//    - Hold pending action signal (what button was clicked)
//    - Hold close request signal (form wants to close)
//  NOT Injectable at root — each DialogLayer owns its instance.
// ─────────────────────────────────────────────────────────────
import { signal } from '@angular/core';
import { DialogActionBridge } from '@/share/model/dialog.types';

export class DialogActionBridgeImpl implements DialogActionBridge {
    private readonly _pendingAction = signal<{ action: string } | null>(null);
    private readonly _closeRequest  = signal<{ result: any } | null>(null);

    readonly pendingAction = this._pendingAction.asReadonly();
    readonly closeRequest  = this._closeRequest.asReadonly();

    /** Dispatch an action. Re-triggers even if same action fires twice. */
    dispatch(action: string): void {
        // Reset first so the same action fired twice still re-triggers the effect
        this._pendingAction.set(null);
        // Use setTimeout to ensure Angular picks up the signal change in next tick
        setTimeout(() => this._pendingAction.set({ action }), 0);
    }

    resetAction(): void {
        this._pendingAction.set(null);
    }

    requestClose(result: any): void {
        this._closeRequest.set({ result });
    }

    reset(): void {
        this._pendingAction.set(null);
        this._closeRequest.set(null);
    }
}
